import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  ColDef,
  GetContextMenuItemsParams,
  GridReadyEvent,
  ICellRendererParams,
  IServerSideGetRowsParams,
  PaginationChangedEvent,
  SortModelItem,
} from '@ag-grid-community/core'
import { ModuleRegistry } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import {
  Button,
  Input,
  Modal,
  Select,
  Tag,
} from '@surecompanies/core_components'
import DatePicker from 'antd/es/date-picker'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import { useDebounce } from 'hooks/useDebounce'
import { relativeTimeString } from 'enrollment-platform/utils/relative-time-string'
import { PlanOverrideAction } from '../../../enums/plan-override-action.enum'
import type {
  PlanAndRateLevelHistoryEntryDto,
  PlanAndRateLevelHistoryQueryDto,
  PlanAndRateLevelHistorySortField,
} from '../../../api/dto/plan-and-rate-level-history.dto'
import {
  listPlanAndRateLevelHistory,
  PLAN_AND_RATE_OVERRIDE_RULE_BASE,
} from '../../../api/plan-and-rate-override-rules-api'
import { useDeleteHistoryEntry } from '../../../hooks/useDeleteHistoryEntry'
import { useDeleteHistoryByFilter } from '../../../hooks/useDeleteHistoryByFilter'
import { US_STATE_OPTIONS } from '../../../constants'
import styles from '../../../components/PlanOverridesComponents.module.scss'
import { numFormatter } from './planAndRateOverrideForm.helpers'

ModuleRegistry.registerModules([ServerSideRowModelModule])

const { RangePicker } = DatePicker

const boolFormatter = (p: { value: unknown }) =>
  p.value == null ? '—' : p.value === true ? '✓' : '✗'

const RA_KEYS = Array.from(
  { length: 67 },
  (_, i) => `RA${String(i + 1).padStart(2, '0')}`
)

const HIOS_DEBOUNCE_MS = 400
const DEFAULT_PAGE_SIZE = 20
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

const ACTION_TAG_PROPS: Partial<
  Record<PlanOverrideAction, { color: string; label: string }>
> = {
  [PlanOverrideAction.OVERRIDE_CREATED]: { color: 'green', label: 'Created' },
  [PlanOverrideAction.OVERRIDE_UPDATED]: { color: 'blue', label: 'Updated' },
  [PlanOverrideAction.OVERRIDE_STATUS_CHANGED]: {
    color: 'orange',
    label: 'Status changed',
  },
  [PlanOverrideAction.OVERRIDE_DELETED]: { color: 'red', label: 'Deleted' },
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function formatActorName(name: string | null): string {
  if (!name) return '—'
  if (UUID_RE.test(name)) return `${name.slice(0, 8)}…`
  return name
}

const ActionCellRenderer = ({
  data,
}: ICellRendererParams<PlanAndRateLevelHistoryEntryDto>) => {
  if (!data) return null
  const known = ACTION_TAG_PROPS[data.action as PlanOverrideAction]
  const isDocument = (data.action as string).includes('DOCUMENT')
  const tagColor = known?.color ?? (isDocument ? 'purple' : 'default')
  const tagLabel = known?.label ?? (isDocument ? 'Document' : 'Other')
  return <Tag color={tagColor}>{tagLabel}</Tag>
}

const ActorCellRenderer = ({
  data,
}: ICellRendererParams<PlanAndRateLevelHistoryEntryDto>) => {
  if (!data) return null
  return (
    <span className={styles.historyActorCell}>
      {formatActorName(data.actorName)}
    </span>
  )
}

const DEFAULT_COL_DEF: ColDef = {
  sortable: false,
  filter: false,
  resizable: true,
}

interface Props {
  onTotalChange?: (total: number) => void
  defaultPageSize?: number
  onPageSizeChange?: (size: number) => void
  filterState: string | undefined
  filterHiosPlanId: string
  onStateChange: (v: string | undefined) => void
  onHiosPlanIdChange: (v: string) => void
}

export default function PlanAndRateLevelHistoryContent({
  onTotalChange,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  onPageSizeChange,
  filterState,
  filterHiosPlanId,
  onStateChange,
  onHiosPlanIdChange,
}: Props) {
  const [hiosPlanIdInput, setHiosPlanIdInput] = useState(filterHiosPlanId)
  const [dateRange, setDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(() => [dayjs().startOf('month'), dayjs().endOf('day')])

  const debouncedHiosPlanId = useDebounce(hiosPlanIdInput, HIOS_DEBOUNCE_MS)

  const onHiosPlanIdChangeRef = useRef(onHiosPlanIdChange)
  useEffect(() => {
    onHiosPlanIdChangeRef.current = onHiosPlanIdChange
  }, [onHiosPlanIdChange])

  const isInitialMountRef = useRef(true)
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      return
    }
    onHiosPlanIdChangeRef.current(debouncedHiosPlanId)
  }, [debouncedHiosPlanId])

  const effectiveHiosPlanId =
    debouncedHiosPlanId.trim().length >= 2
      ? debouncedHiosPlanId.trim()
      : undefined

  const filters = useMemo<PlanAndRateLevelHistoryQueryDto>(
    () => ({
      state: filterState,
      hiosPlanId: effectiveHiosPlanId,
      occurredFrom: dateRange?.[0]?.toISOString() ?? undefined,
      occurredTo: dateRange?.[1]?.toISOString() ?? undefined,
    }),
    [filterState, effectiveHiosPlanId, dateRange]
  )

  const [total, setTotal] = useState(0)

  const onTotalChangeRef = useRef(onTotalChange)
  useEffect(() => {
    onTotalChangeRef.current = onTotalChange
  }, [onTotalChange])

  const filtersRef = useRef(filters)
  useEffect(() => {
    filtersRef.current = filters
  }, [filters])

  const gridInitializedRef = useRef(false)

  const handleFirstDataRendered = useCallback(() => {
    gridInitializedRef.current = true
  }, [])

  const handlePaginationChanged = useCallback(
    (event: PaginationChangedEvent<PlanAndRateLevelHistoryEntryDto>) => {
      if (event.newPageSize && gridInitializedRef.current) {
        onPageSizeChange?.(event.api.paginationGetPageSize())
      }
    },
    [onPageSizeChange]
  )

  const [grid, setGrid] =
    useState<GridReadyEvent<PlanAndRateLevelHistoryEntryDto>>()

  const datasource = useMemo(
    () => ({
      getRows: async (
        params: IServerSideGetRowsParams<PlanAndRateLevelHistoryEntryDto>
      ) => {
        try {
          const { startRow, endRow, sortModel } = params.request
          const pageSize = (endRow ?? DEFAULT_PAGE_SIZE) - (startRow ?? 0)
          const page = Math.floor((startRow ?? 0) / pageSize) + 1
          const sort = (sortModel as SortModelItem[])?.[0]
          const sortBy = sort?.colId as
            | PlanAndRateLevelHistorySortField
            | undefined
          const sortOrder = sort?.sort as 'asc' | 'desc' | undefined

          const result = await listPlanAndRateLevelHistory({
            ...filtersRef.current,
            page,
            limit: pageSize,
            ...(sortBy && sortOrder ? { sortBy, sortOrder } : {}),
          })

          onTotalChangeRef.current?.(result.meta.total)
          setTotal(result.meta.total)
          params.success({
            rowData: result.data,
            rowCount: result.meta.total,
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to load plan and rate level history', error)
          params.fail()
        }
      },
    }),
    [filters]
  )

  useEffect(() => {
    if (!grid) return
    grid.api.setGridOption('serverSideDatasource', datasource)
    grid.api.refreshServerSide({ purge: true })
  }, [grid, datasource])

  const { mutateAsync: deleteEntry } = useDeleteHistoryEntry(
    PLAN_AND_RATE_OVERRIDE_RULE_BASE
  )
  const { mutateAsync: deleteByFilter } = useDeleteHistoryByFilter(
    PLAN_AND_RATE_OVERRIDE_RULE_BASE
  )

  const handleDeleteRow = useCallback(
    (logId: string) => {
      Modal.confirm({
        title: 'Delete history entry?',
        content: 'This action cannot be undone.',
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk: async () => {
          await deleteEntry(logId)
          grid?.api.refreshServerSide({ purge: true })
        },
      })
    },
    [deleteEntry, grid]
  )

  const handleDeleteByFilter = useCallback(() => {
    const hasFilters = Object.values(filtersRef.current).some(
      (v) => v !== undefined
    )
    Modal.confirm({
      title: 'Delete history entries?',
      content: hasFilters
        ? 'Delete all matching history entries? This action cannot be undone.'
        : 'Delete ALL history entries? This action cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        await deleteByFilter(filtersRef.current as Record<string, unknown>)
        grid?.api.refreshServerSide({ purge: true })
      },
    })
  }, [deleteByFilter, grid])

  const handleRangeChange = useCallback(
    (values: [Dayjs | null, Dayjs | null] | null) => {
      setDateRange(values)
    },
    []
  )

  const disabledDate = useCallback(
    (current: Dayjs, info?: { from?: Dayjs }) => {
      if (info?.from) {
        return Math.abs(current.diff(info.from, 'days')) >= 31
      }
      return false
    },
    []
  )

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<PlanAndRateLevelHistoryEntryDto>) => [
      {
        name: 'Copy Cell',
        action: () => {
          const value = params.value
          if (value != null) void navigator.clipboard.writeText(String(value))
        },
      },
    ],
    []
  )

  const columnDefs = useMemo<ColDef<PlanAndRateLevelHistoryEntryDto>[]>(
    () => [
      {
        headerName: 'Action',
        field: 'action',
        cellRenderer: ActionCellRenderer,
        width: 140,
      },
      {
        headerName: 'Status',
        field: 'status',
        cellRenderer: ({
          data: row,
        }: ICellRendererParams<PlanAndRateLevelHistoryEntryDto>) => {
          if (!row) return null
          return (
            <Tag
              color={
                row.status === 'APPROVED'
                  ? 'green'
                  : row.status === 'REJECTED'
                    ? 'red'
                    : 'gold'
              }
            >
              {row.status === 'APPROVED'
                ? 'Approved'
                : row.status === 'REJECTED'
                  ? 'Rejected'
                  : 'Draft'}
            </Tag>
          )
        },
        width: 110,
      },
      {
        headerName: 'Occurred',
        field: 'occurredAt',
        valueFormatter: (p) =>
          p.value ? relativeTimeString(p.value as string, 30) : '—',
        sort: 'desc',
        sortable: true,
        width: 150,
      },
      {
        headerName: 'Year',
        field: 'matchYear',
        cellRenderer: ({ value }: ICellRendererParams) =>
          value !== undefined && value !== null ? (
            <span>{String(value)}</span>
          ) : (
            <span className={styles.historyEmptyCell}>—</span>
          ),
        width: 80,
      },
      {
        headerName: 'State',
        field: 'matchState',
        cellRenderer: ({ value }: ICellRendererParams) =>
          value ? (
            <span>{value as string}</span>
          ) : (
            <span className={styles.historyEmptyCell}>—</span>
          ),
        width: 80,
      },
      {
        headerName: 'HIOS Plan ID',
        field: 'matchHiosPlanId',
        cellRenderer: ({ value }: ICellRendererParams) =>
          value ? (
            <span>{value as string}</span>
          ) : (
            <span className={styles.historyEmptyCell}>—</span>
          ),
        width: 160,
      },
      {
        headerName: 'Plan Name',
        valueGetter: (p) => p.data?.params?.planName ?? null,
        valueFormatter: (p) => p.value ?? '—',
        minWidth: 160,
      },
      {
        headerName: 'Carrier',
        valueGetter: (p) => p.data?.params?.carrier ?? null,
        valueFormatter: (p) => p.value ?? '—',
        minWidth: 130,
      },
      {
        headerName: 'Carrier Friendly Name',
        valueGetter: (p) => p.data?.params?.householdName ?? null,
        valueFormatter: (p) => p.value ?? '—',
        minWidth: 160,
      },
      {
        headerName: 'Plan Type',
        valueGetter: (p) => p.data?.params?.planType ?? null,
        valueFormatter: (p) => p.value ?? '—',
        minWidth: 100,
      },
      {
        headerName: 'Metal Level',
        valueGetter: (p) => p.data?.params?.level ?? null,
        valueFormatter: (p) => p.value ?? '—',
        minWidth: 110,
      },
      {
        headerName: 'Service Area ID',
        valueGetter: (p) => p.data?.params?.serviceAreaId ?? null,
        valueFormatter: (p) => p.value ?? '—',
        minWidth: 130,
      },
      {
        headerName: 'For Sale',
        valueGetter: (p) => p.data?.params?.isForSale ?? null,
        valueFormatter: boolFormatter,
        minWidth: 90,
      },
      {
        headerName: 'HSA',
        valueGetter: (p) => p.data?.params?.hsa ?? null,
        valueFormatter: boolFormatter,
        minWidth: 70,
      },
      {
        headerName: 'Network',
        valueGetter: (p) => p.data?.params?.networkName ?? null,
        valueFormatter: (p) => p.value ?? '—',
        minWidth: 130,
      },
      {
        headerName: 'On Exchange',
        valueGetter: (p) => p.data?.params?.onExchange ?? null,
        valueFormatter: boolFormatter,
        minWidth: 110,
      },
      {
        headerName: 'Off Exchange',
        valueGetter: (p) => p.data?.params?.offExchange ?? null,
        valueFormatter: boolFormatter,
        minWidth: 110,
      },
      {
        headerName: 'Plan Status',
        valueGetter: (p) => p.data?.params?.planStatus ?? null,
        valueFormatter: (p) => p.value ?? '—',
        minWidth: 110,
      },
      {
        headerName: 'Crosswalk Plan ID',
        valueGetter: (p) => p.data?.params?.crosswalkPlanId ?? null,
        valueFormatter: (p) => p.value ?? '—',
        minWidth: 150,
      },
      {
        headerName: 'Base Rate',
        valueGetter: (p) => p.data?.params?.baseRate ?? null,
        valueFormatter: numFormatter,
        minWidth: 100,
      },
      {
        headerName: 'Plan Fee',
        valueGetter: (p) => p.data?.params?.planFee ?? null,
        valueFormatter: numFormatter,
        minWidth: 100,
      },
      {
        headerName: 'Proof of Residence',
        valueGetter: (p) => p.data?.params?.proofOfResidenceRequired ?? null,
        valueFormatter: boolFormatter,
        minWidth: 150,
      },
      ...RA_KEYS.map((raKey) => ({
        colId: raKey,
        headerName: raKey,
        valueGetter: (p: { data?: PlanAndRateLevelHistoryEntryDto }) =>
          p.data?.params?.rateAdjustments?.[raKey] ?? null,
        valueFormatter: numFormatter,
        minWidth: 80,
      })),
      {
        headerName: 'Actor',
        cellRenderer: ActorCellRenderer,
        flex: 1,
        minWidth: 180,
      },
      {
        headerName: '',
        colId: 'rowActions',
        width: 52,
        resizable: false,
        sortable: false,
        cellRenderer: ({
          data,
        }: ICellRendererParams<PlanAndRateLevelHistoryEntryDto>) => {
          if (!data) return null
          return (
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteRow(data.id)}
            />
          )
        },
      },
    ],
    [handleDeleteRow]
  )

  return (
    <>
      <div className={styles.historyFilterBar}>
        {total > 0 && (
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleDeleteByFilter}
            style={{ marginRight: 'auto' }}
          >
            Delete
          </Button>
        )}
        <Select
          placeholder="Filter by State"
          allowClear
          showSearch
          optionFilterProp="label"
          options={US_STATE_OPTIONS}
          value={filterState}
          onChange={(v: string | undefined) => onStateChange(v)}
          style={{ width: 200 }}
        />
        <Input
          placeholder="Filter by HIOS Plan ID"
          value={hiosPlanIdInput}
          allowClear
          className={styles.historyFilterInput}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setHiosPlanIdInput(e.target.value)
          }
        />
        <RangePicker
          value={dateRange}
          onChange={handleRangeChange}
          disabledDate={disabledDate}
          placeholder={['From', 'To']}
          allowClear
          className={styles.historyFilterDatePicker}
        />
      </div>

      <div className={`ag-theme-alpine ${styles.historyGridAutoHeight}`}>
        <AgGridReact
          getRowId={({ data }) => data.id}
          rowModelType="serverSide"
          columnDefs={columnDefs}
          defaultColDef={DEFAULT_COL_DEF}
          enableCellTextSelection
          suppressRowClickSelection
          suppressColumnVirtualisation
          getContextMenuItems={getContextMenuItems}
          overlayLoadingTemplate="Loading..."
          overlayNoRowsTemplate="No history entries yet"
          domLayout="autoHeight"
          rowHeight={52}
          pagination
          paginationPageSize={defaultPageSize}
          paginationPageSizeSelector={PAGE_SIZE_OPTIONS}
          cacheBlockSize={defaultPageSize}
          onGridReady={setGrid}
          onFirstDataRendered={handleFirstDataRendered}
          onPaginationChanged={handlePaginationChanged}
        />
      </div>
    </>
  )
}
